/**
 * Platform-agnostic MIDI service that uses CoreMIDI on iOS and WebMIDI on web
 */
import { Capacitor } from '@capacitor/core';
import { WebMidi, type Input, type Output } from 'webmidi';
import { CoreMidi } from '../../../plugins/capacitor-coremidi/src';
import type { MidiMessage } from '../../../plugins/capacitor-coremidi/src/definitions';

export type MidiDeviceType = 'input' | 'output' | 'both';

export interface UnifiedMidiDevice {
  id: string;
  name: string;
  manufacturer: string;
  type: MidiDeviceType;
  isConnected: boolean;
  // Platform-specific references
  webMidiInput?: Input;
  webMidiOutput?: Output;
  coreMidiId?: string;
  outputId?: string; // For bidirectional CoreMIDI devices - the destination endpoint ID
}

export interface MidiServiceOptions {
  sysex?: boolean;
  onDeviceConnected?: (device: UnifiedMidiDevice) => void;
  onDeviceDisconnected?: (device: UnifiedMidiDevice) => void;
  onMidiMessage?: (message: MidiMessage) => void;
  onError?: (error: Error) => void;
}

export class MidiService {
  private isIOS = Capacitor.getPlatform() === 'ios';
  private isInitialized = false;
  private options: MidiServiceOptions;
  private deviceMap = new Map<string, UnifiedMidiDevice>();

  constructor(options: MidiServiceOptions = {}) {
    this.options = options;
  }

  /**
   * Initialize the MIDI service for the current platform
   */
  async initialize(): Promise<boolean> {
    if (this.isInitialized) {
      return true;
    }

    try {
      if (this.isIOS) {
        // Initialize CoreMIDI on iOS
        const result = await CoreMidi.initialize();

        if (result.success) {
          // Set up CoreMIDI listeners
          await this.setupCoreMidiListeners();
          this.isInitialized = true;
          return true;
        } else {
          throw new Error('Failed to initialize CoreMIDI');
        }
      } else {
        // Initialize WebMIDI on web/other platforms
        await WebMidi.enable({ sysex: this.options.sysex });

        // Set up WebMIDI listeners
        this.setupWebMidiListeners();
        this.isInitialized = true;
        return true;
      }
    } catch (error) {
      const err = error as Error;
      console.error('Failed to initialize MIDI:', err);
      this.options.onError?.(err);
      return false;
    }
  }

  /**
   * Get all available MIDI devices
   */
  async getDevices(): Promise<UnifiedMidiDevice[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    this.deviceMap.clear();

    if (this.isIOS) {
      const { devices } = await CoreMidi.getDevices();

      return devices.map(device => {
        const unifiedDevice: UnifiedMidiDevice = {
          id: device.id,
          name: device.name,
          manufacturer: device.manufacturer,
          type: device.type,
          isConnected: device.isConnected,
          coreMidiId: device.id,
          outputId: (device as any).outputId // Preserve outputId for bidirectional devices
        };

        this.deviceMap.set(device.id, unifiedDevice);
        return unifiedDevice;
      });
    } else {
      const devices: UnifiedMidiDevice[] = [];

      // Process WebMIDI inputs
      WebMidi.inputs.forEach(input => {
        const device: UnifiedMidiDevice = {
          id: input.id,
          name: input.name,
          manufacturer: input.manufacturer || 'Unknown',
          type: 'input',
          isConnected: true,
          webMidiInput: input
        };
        devices.push(device);
        this.deviceMap.set(input.id, device);
      });

      // Process WebMIDI outputs
      WebMidi.outputs.forEach(output => {
        const existingDevice = this.deviceMap.get(output.id);

        if (existingDevice) {
          // Device has both input and output
          existingDevice.type = 'both';
          existingDevice.webMidiOutput = output;
        } else {
          const device: UnifiedMidiDevice = {
            id: output.id,
            name: output.name,
            manufacturer: output.manufacturer || 'Unknown',
            type: 'output',
            isConnected: true,
            webMidiOutput: output
          };
          devices.push(device);
          this.deviceMap.set(output.id, device);
        }
      });

      return devices;
    }
  }

  /**
   * Connect to a MIDI device
   */
  async connectDevice(deviceId: string): Promise<boolean> {
    if (this.isIOS) {
      const result = await CoreMidi.connectDevice({ deviceId });
      return result.success;
    } else {
      // WebMIDI devices are automatically connected
      return this.deviceMap.has(deviceId);
    }
  }

  /**
   * Disconnect from a MIDI device
   */
  async disconnectDevice(deviceId: string): Promise<boolean> {
    if (this.isIOS) {
      const result = await CoreMidi.disconnectDevice({ deviceId });
      return result.success;
    } else {
      // WebMIDI doesn't have explicit disconnect
      return true;
    }
  }

  /**
   * Send a note on message
   */
  async sendNoteOn(
    deviceId: string,
    channel: number,
    note: number,
    velocity: number
  ): Promise<void> {
    if (this.isIOS) {
      await CoreMidi.sendNoteOn({ deviceId, channel, note, velocity });
    } else {
      const device = this.deviceMap.get(deviceId);
      if (device?.webMidiOutput) {
        // WebMIDI uses 1-based channel numbering and 0-1 range for attack
        device.webMidiOutput.sendNoteOn(note, {
          channels: channel + 1,
          attack: velocity / 127
        });
      }
    }
  }

  /**
   * Send a note off message
   */
  async sendNoteOff(
    deviceId: string,
    channel: number,
    note: number,
    velocity: number = 0
  ): Promise<void> {
    if (this.isIOS) {
      await CoreMidi.sendNoteOff({ deviceId, channel, note, velocity });
    } else {
      const device = this.deviceMap.get(deviceId);
      if (device?.webMidiOutput) {
        device.webMidiOutput.sendNoteOff(note, {
          channels: channel + 1,
          release: velocity / 127
        });
      }
    }
  }

  /**
   * Send a control change message
   */
  async sendControlChange(
    deviceId: string,
    channel: number,
    controller: number,
    value: number
  ): Promise<void> {
    if (this.isIOS) {
      await CoreMidi.sendControlChange({ deviceId, channel, controller, value });
    } else {
      const device = this.deviceMap.get(deviceId);
      if (device?.webMidiOutput) {
        device.webMidiOutput.sendControlChange(controller, value, {
          channels: channel + 1
        });
      }
    }
  }

  /**
   * Send a program change message
   */
  async sendProgramChange(
    deviceId: string,
    channel: number,
    program: number
  ): Promise<void> {
    if (this.isIOS) {
      await CoreMidi.sendProgramChange({ deviceId, channel, program });
    } else {
      const device = this.deviceMap.get(deviceId);
      if (device?.webMidiOutput) {
        device.webMidiOutput.sendProgramChange(program, {
          channels: channel + 1
        });
      }
    }
  }

  /**
   * Send MIDI clock start
   */
  async sendClockStart(deviceId: string): Promise<void> {
    if (this.isIOS) {
      await CoreMidi.sendClockStart({ deviceId });
    } else {
      const device = this.deviceMap.get(deviceId);
      if (device?.webMidiOutput) {
        device.webMidiOutput.sendStart();
      }
    }
  }

  /**
   * Send MIDI clock stop
   */
  async sendClockStop(deviceId: string): Promise<void> {
    if (this.isIOS) {
      await CoreMidi.sendClockStop({ deviceId });
    } else {
      const device = this.deviceMap.get(deviceId);
      if (device?.webMidiOutput) {
        device.webMidiOutput.sendStop();
      }
    }
  }

  /**
   * Send MIDI clock tick
   */
  async sendClockTick(deviceId: string): Promise<void> {
    if (this.isIOS) {
      await CoreMidi.sendClockTick({ deviceId });
    } else {
      const device = this.deviceMap.get(deviceId);
      if (device?.webMidiOutput) {
        device.webMidiOutput.sendClock();
      }
    }
  }

  /**
   * Start listening for MIDI input
   */
  async startListening(deviceId?: string): Promise<void> {
    if (this.isIOS) {
      await CoreMidi.startListening({ deviceId });
    } else {
      if (deviceId) {
        const device = this.deviceMap.get(deviceId);
        if (device?.webMidiInput) {
          this.setupWebMidiInputListeners(device.webMidiInput);
        }
      } else {
        // Listen to all inputs
        WebMidi.inputs.forEach(input => {
          this.setupWebMidiInputListeners(input);
        });
      }
    }
  }

  /**
   * Stop listening for MIDI input
   */
  async stopListening(deviceId?: string): Promise<void> {
    if (this.isIOS) {
      await CoreMidi.stopListening({ deviceId });
    } else {
      if (deviceId) {
        const device = this.deviceMap.get(deviceId);
        if (device?.webMidiInput) {
          device.webMidiInput.removeListener();
        }
      } else {
        // Stop listening to all inputs
        WebMidi.inputs.forEach(input => {
          input.removeListener();
        });
      }
    }
  }

  /**
   * Check if MIDI is available on the current platform
   */
  async isAvailable(): Promise<boolean> {
    if (this.isIOS) {
      // CoreMIDI is always available on iOS
      return true;
    } else {
      // Check for WebMIDI support
      const result = await CoreMidi.isWebMidiAvailable();
      return result.available;
    }
  }

  /**
   * Clean up and release resources
   */
  async destroy(): Promise<void> {
    if (!this.isInitialized) return;

    if (this.isIOS) {
      await CoreMidi.removeAllListeners();
    } else {
      WebMidi.disable();
    }

    this.deviceMap.clear();
    this.isInitialized = false;
  }

  // Private methods

  private async setupCoreMidiListeners(): Promise<void> {
    // Listen for MIDI messages
    await CoreMidi.addListener('midiMessage', (message) => {
      this.options.onMidiMessage?.(message);
    });

    // Listen for device connections
    await CoreMidi.addListener('deviceConnected', async (device) => {
      await this.getDevices(); // Refresh device list
      const unifiedDevice = this.deviceMap.get(device.id);
      if (unifiedDevice) {
        this.options.onDeviceConnected?.(unifiedDevice);
      }
    });

    // Listen for device disconnections
    await CoreMidi.addListener('deviceDisconnected', async (device) => {
      const unifiedDevice = this.deviceMap.get(device.id);
      if (unifiedDevice) {
        this.options.onDeviceDisconnected?.(unifiedDevice);
      }
      await this.getDevices(); // Refresh device list
    });

    // Listen for errors
    await CoreMidi.addListener('midiError', (error) => {
      this.options.onError?.(new Error(`${error.code}: ${error.message}`));
    });
  }

  private setupWebMidiListeners(): void {
    // Listen for device connections
    WebMidi.addListener('connected', async () => {
      const oldDevices = new Set(this.deviceMap.keys());
      const devices = await this.getDevices();

      // Find newly connected device
      devices.forEach(device => {
        if (!oldDevices.has(device.id)) {
          this.options.onDeviceConnected?.(device);
        }
      });
    });

    // Listen for device disconnections
    WebMidi.addListener('disconnected', async () => {
      const oldDevices = new Map(this.deviceMap);
      const devices = await this.getDevices();
      const currentIds = new Set(devices.map(d => d.id));

      // Find disconnected devices
      oldDevices.forEach((device, id) => {
        if (!currentIds.has(id)) {
          this.options.onDeviceDisconnected?.(device);
        }
      });
    });
  }

  private setupWebMidiInputListeners(input: Input): void {
    // Clear existing listeners first
    input.removeListener();

    // Note on/off
    input.addListener('noteon', (e) => {
      this.options.onMidiMessage?.({
        type: 'noteOn',
        channel: e.message.channel - 1,  // Convert to 0-based
        note: e.note.number,
        velocity: Math.round(e.note.rawAttack * 127),
        timestamp: e.timestamp,
        deviceId: input.id
      });
    });

    input.addListener('noteoff', (e) => {
      this.options.onMidiMessage?.({
        type: 'noteOff',
        channel: e.message.channel - 1,
        note: e.note.number,
        velocity: Math.round(e.note.rawRelease * 127),
        timestamp: e.timestamp,
        deviceId: input.id
      });
    });

    // Control change
    input.addListener('controlchange', (e) => {
      this.options.onMidiMessage?.({
        type: 'controlChange',
        channel: e.message.channel - 1,
        controller: e.controller.number,
        value: e.rawValue,
        timestamp: e.timestamp,
        deviceId: input.id
      });
    });

    // Program change
    input.addListener('programchange', (e) => {
      this.options.onMidiMessage?.({
        type: 'programChange',
        channel: e.message.channel - 1,
        program: typeof e.value === 'number' ? e.value : 0,
        timestamp: e.timestamp,
        deviceId: input.id
      });
    });

    // MIDI Clock
    input.addListener('clock', (e) => {
      this.options.onMidiMessage?.({
        type: 'clock',
        timestamp: e.timestamp,
        deviceId: input.id
      });
    });

    input.addListener('start', (e) => {
      this.options.onMidiMessage?.({
        type: 'start',
        timestamp: e.timestamp,
        deviceId: input.id
      });
    });

    input.addListener('stop', (e) => {
      this.options.onMidiMessage?.({
        type: 'stop',
        timestamp: e.timestamp,
        deviceId: input.id
      });
    });

    input.addListener('continue', (e) => {
      this.options.onMidiMessage?.({
        type: 'continue',
        timestamp: e.timestamp,
        deviceId: input.id
      });
    });
  }
}